import type { FC } from "react";
import { view } from "@orderofchaos/mvvm";
import { Layout } from "@/components/Layout/Layout";
import Icon, { IconSize } from "@/components/Icon/Icon";
import Button, { ButtonDesign } from "@/components/Button/Button";
import { formatTypeTag, normalizeTypeTags } from "@/utils/typeTags";
import {
  BoxesListPageViewModel,
  type BoxesListPageViewProps,
} from "./BoxesListPageViewModel";
import { VirtualizedChunks } from "@/components/VirtualizedChunks/VirtualizedChunks";
import styles from "./BoxesListPage.module.css";

interface BoxesListPageProps extends BoxesListPageViewProps {}

export const BoxesListPage: FC<BoxesListPageProps> = view(
  BoxesListPageViewModel,
)<BoxesListPageProps>(({
  viewModel: {
    pageTitle,
    summaryLabel,
    items,
    error,
    hasMore,
    canAutoLoadMore,
    isLoadingInitial,
    isLoadingMore,
    refresh,
    loadMore,
    openBox,
    openTypeTag,
    openParentReference,
    resolveParentLabel,
  },
}) => {
  return (
    <Layout title={pageTitle}>
      <section className={styles.root}>
        <div className={styles.toolbar}>
          <div className={styles.titleBlock}>
            <strong className={styles.title}>{pageTitle}</strong>
            <span className={styles.subtitle}>{summaryLabel}</span>
          </div>
          <Button
            design={ButtonDesign.ghost}
            className={styles.refresh}
            onClick={() => void refresh()}
            disabled={isLoadingInitial || isLoadingMore}
          >
            обновить
          </Button>
        </div>

        {error && (
          <div className={styles.error}>
            <span>{error}</span>
            <Button
              design={ButtonDesign.basic}
              className={styles.retry}
              onClick={() => void refresh()}
            >
              повторить
            </Button>
          </div>
        )}

        {isLoadingInitial ? (
          <div className={styles.stateCard}>
            <Icon
              name={"Bag"}
              size={IconSize.large}
              className={styles.stateIcon}
              loading
            />
            <strong>Загружаем коробки</strong>
            <span>Подготавливаем первую страницу списка</span>
          </div>
        ) : items.length === 0 ? (
          <div className={styles.stateCard}>
            <Icon
              name={"Bag"}
              size={IconSize.large}
              className={styles.stateIcon}
            />
            <strong>Коробки не найдены</strong>
            <span>Список пока пуст</span>
          </div>
        ) : (
          <VirtualizedChunks
            items={items}
            chunkSize={8}
            windowChunks={2}
            hasMore={canAutoLoadMore}
            isLoadingMore={isLoadingMore}
            className={styles.virtualList}
            contentClassName={styles.virtualContent}
            loadingLabel={"Загружаем следующую страницу..."}
            onLoadMore={() => void loadMore()}
            renderChunk={(chunkId, chunkItems) => (
              <ul
                key={chunkId}
                className={styles.list}
                aria-label={`Коробки, блок ${chunkId + 1}`}
              >
                {chunkItems.map((item) => {
                  const typeTags = normalizeTypeTags(item.typeTags);

                  return (
                    <li key={item.id} className={styles.item}>
                      <article className={styles.card}>
                        <button
                          type="button"
                          className={styles.cardOpen}
                          onClick={() => openBox(item)}
                        >
                          <div className={styles.preview}>
                            {item.photo ? (
                              <img
                                src={item.photo}
                                alt={item.name}
                                loading="lazy"
                                decoding="async"
                              />
                            ) : (
                              <div className={styles.placeholder}>
                                <Icon name={"Bag"} size={IconSize.big} />
                              </div>
                            )}
                          </div>
                          <div className={styles.content}>
                            <div className={styles.cardHeader}>
                              <strong className={styles.itemTitle}>
                                {item.name || "Без названия"}
                              </strong>
                              <span className={styles.kindBadge}>
                                <Icon name={"Bag"} size={IconSize.tiny} />
                                коробка
                              </span>
                            </div>
                            <p className={styles.description}>
                              {item.description?.trim() || "Без описания"}
                            </p>
                          </div>
                        </button>
                        <div className={styles.metaRow}>
                          {typeTags.length > 0 ? (
                            typeTags.map((tag) => (
                              <button
                                key={`${item.id}-tag-${tag}`}
                                type="button"
                                className={`${styles.metaTag} ${styles.metaTagButton}`}
                                onClick={() => openTypeTag(tag)}
                              >
                                {formatTypeTag(tag)}
                              </button>
                            ))
                          ) : (
                            <span className={styles.metaTag}>без тега</span>
                          )}
                          <button
                            type="button"
                            className={`${styles.metaTag} ${styles.metaTagButton}`}
                            onClick={() => openParentReference(item)}
                          >
                            {resolveParentLabel(item)}
                          </button>
                        </div>
                      </article>
                    </li>
                  );
                })}
              </ul>
            )}
          />
        )}
        {!isLoadingInitial &&
          !isLoadingMore &&
          !hasMore &&
          items.length > 0 && (
            <p className={styles.loadMoreState}>Показаны все коробки</p>
          )}
      </section>
    </Layout>
  );
});
